import React, { useState, useEffect, useRef } from 'react';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import './CanvasNameDisplay.css';

export interface CanvasNameDisplayProps {
  className?: string;
}

const CanvasNameDisplay: React.FC<CanvasNameDisplayProps> = ({ className = '' }) => {
  // Access canvasName directly from state for reactive updates
  const canvasName = useWhiteboardStore(state => state.state.canvasName);
  const { setCanvasName } = useWhiteboardStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [hasEverBeenSaved, setHasEverBeenSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Display name logic - FIXED: Use canvasName if available, regardless of hasEverBeenSaved
  const displayName = canvasName || 'Untitled Canvas';

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Listen for save/load events to update saved state
  useEffect(() => {
    const handleSaveEvent = (event: CustomEvent) => {
      setHasEverBeenSaved(true);
    };

    const handleLoadEvent = (event: CustomEvent) => {
      setHasEverBeenSaved(true);
    };

    // Listen for custom save/load events
    window.addEventListener('whiteboard-saved', handleSaveEvent as EventListener);
    window.addEventListener('whiteboard-loaded', handleLoadEvent as EventListener);

    return () => {
      window.removeEventListener('whiteboard-saved', handleSaveEvent as EventListener);
      window.removeEventListener('whiteboard-loaded', handleLoadEvent as EventListener);
    };
  }, [setCanvasName]);

  const handleStartEdit = () => {
    // Allow editing if we have a canvas name OR if we've been saved before
    if (!hasEverBeenSaved && !canvasName) {
      return; // Don't allow editing untitled canvas
    }
    
    const nameToEdit = canvasName || displayName;
    setEditingName(nameToEdit);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const trimmedName = editingName.trim();
    
    if (trimmedName && trimmedName !== canvasName) {
      setCanvasName(trimmedName);
      
      // Dispatch canvas rename event for other components to listen
      window.dispatchEvent(new CustomEvent('canvas-renamed', {
        detail: { oldName: canvasName, newName: trimmedName }
      }));
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditingName('');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleInputBlur = () => {
    handleSaveEdit();
  };

  return (
    <div className={`canvas-name-display ${className}`}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editingName}
          onChange={(e) => setEditingName(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          className="canvas-name-input"
          placeholder="Enter canvas name..."
          maxLength={50}
        />
      ) : (
        <div 
          className={`canvas-name-text ${(hasEverBeenSaved || canvasName) ? 'editable' : 'not-editable'}`}
          onClick={handleStartEdit}
          title={(hasEverBeenSaved || canvasName) ? 'Click to edit canvas name' : 'Save the canvas to set a name'}
        >
          <span className="canvas-name-icon">
            {(hasEverBeenSaved || canvasName) ? '📄' : '📝'}
          </span>
          <span className="canvas-name-label">
            {displayName}
          </span>
          {(hasEverBeenSaved || canvasName) && (
            <span className="canvas-name-edit-hint">✏️</span>
          )}
        </div>
      )}
    </div>
  );
};

export default CanvasNameDisplay;
